import { useCallback, useRef, useState } from 'react';
import { useEmployeeService } from 'domain/shared/employee';
import { Box, Typography } from '@mui/material';
import CareerSectionTitle from '../shared/CareerSectionTitle';
import { FormShowTemplate, FormFieldsData } from 'framework/form';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import SpecializationContent from './SpecializationContent';
import { useShowContext } from 'framework/show';
import usePositionSlotValidationService, {
  EmployeePositionSlotChangeValidationRequest,
  EmployeePositionSlotValidationResultDto,
} from 'domain/people/positionSlots/usePositionSlotValidationService';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { Modal } from 'framework/ui/ui-builder';
import { Button } from 'framework/ui/mui-builder';
import { getNullableString } from 'framework/utils';

type PendingSubmission = {
  values: FormFieldsData;
  resolve: (value: void | object) => void;
  reject: (error: Error) => void;
};

const SpecializationInfoSection = () => {
  const { setData } = useShowContext();
  const { updateSpecializationInfo, getForUpdate } = useEmployeeService();
  const { validateEmployeePositionSlotChange } = usePositionSlotValidationService();
  const { addMessage } = useMessageService();
  const employeeId = useEmployeeId();
  const currentEmployeeId = employeeId || '';
  const [currentPositionSlotId, setCurrentPositionSlotId] = useState<string | null>(null);
  const [warningResult, setWarningResult] = useState<EmployeePositionSlotValidationResultDto | null>(null);
  const pendingSubmissionRef = useRef<PendingSubmission | null>(null);
  const warningCancelledRef = useRef(false);

  const submitSpecializationUpdate = useCallback(
    async (values: FormFieldsData): Promise<void | object> => {
      try {
        const result = await updateSpecializationInfo(currentEmployeeId, values);
        if (result && setData) {
          setData(result);
        }

        return result;
      } catch (error) {
        addMessage(new ErrorMessage('Sorry, worker information cannot be updated'));
        throw error;
      }
    },
    [addMessage, currentEmployeeId, setData, updateSpecializationInfo],
  );

  const buildValidationRequest = useCallback(
    (values: FormFieldsData): EmployeePositionSlotChangeValidationRequest | null => {
      if (!currentPositionSlotId) {
        return null;
      }

      return {
        employeeAlias: currentEmployeeId,
        positionSlotId: currentPositionSlotId,
        employeeData: {
          positionId: Number(values.positionId),
          technologyId: getNullableString(values.specializationInfo?.technologyId),
          secondaryTechnologyId: getNullableString(values.specializationInfo?.secondaryTechnologyId),
          competencyLevel: getNullableString(values.specializationInfo?.competencyLevel),
        },
      };
    },
    [currentEmployeeId, currentPositionSlotId],
  );

  const handleWarningCancel = useCallback(() => {
    warningCancelledRef.current = true;
    const pendingSubmission = pendingSubmissionRef.current;
    pendingSubmissionRef.current = null;
    setWarningResult(null);

    if (pendingSubmission) {
      pendingSubmission.reject(new Error('Position slot validation warning was cancelled'));
    }
  }, []);

  const handleWarningSubmit = useCallback(async () => {
    const pendingSubmission = pendingSubmissionRef.current;
    pendingSubmissionRef.current = null;
    setWarningResult(null);

    if (!pendingSubmission) {
      return;
    }

    try {
      const result = await submitSpecializationUpdate(pendingSubmission.values);
      pendingSubmission.resolve(result);
    } catch (error) {
      pendingSubmission.reject(error as Error);
    }
  }, [submitSpecializationUpdate]);

  const handleSubmit = useCallback(
    async (values: FormFieldsData): Promise<void | object> => {
      warningCancelledRef.current = false;

      let validationResult: EmployeePositionSlotValidationResultDto | null = null;

      try {
        const validationRequest = buildValidationRequest(values);
        if (validationRequest) {
          validationResult = await validateEmployeePositionSlotChange(validationRequest);
        }
      } catch (error) {
        return submitSpecializationUpdate(values);
      }

      if (warningCancelledRef.current) {
        throw new Error('Position slot validation warning was cancelled');
      }

      if (validationResult?.errors?.length) {
        setWarningResult(validationResult);

        return new Promise<void | object>((resolve, reject) => {
          pendingSubmissionRef.current = {
            values,
            resolve,
            reject,
          };
        });
      }

      return submitSpecializationUpdate(values);
    },
    [buildValidationRequest, submitSpecializationUpdate, validateEmployeePositionSlotChange],
  );

  if (!employeeId) {
    return null;
  }

  const warningSubmitButton = (
    <Button key="position-slot-warning-submit" type="button" onClick={handleWarningSubmit}>
      Save anyway
    </Button>
  );

  const warningCancelButton = (
    <Button key="position-slot-warning-cancel" type="button" variant="outlined" onClick={handleWarningCancel}>
      Back to editing
    </Button>
  );

  return (
    <Box position="relative">
      <CareerSectionTitle title="Specialization" />

      <FormShowTemplate
        getDataFunc={() => getForUpdate(employeeId)}
        getValidationSchemaFunc={async () => ({
          positionId: {
            type: 'enum',
            required: true,
          },
          specializationInfo: {
            type: 'object',
            properties: {
              technologyId: {
                type: 'enum',
                required: {
                  value: true,
                  when: [
                    {
                      $positionId: {
                        in: ['47', 47, 21, '21'],
                      },
                    },
                  ],
                },
              },
              competencyLevel: {
                type: 'enum',
              },
            },
          },
        })}
        submitFormFunc={handleSubmit}
        afterSubmit={{
          message: 'Worker information has been successfully updated.',
        }}
        isNeedToHandleErrorsOnSubmit={false}
        isSmallLoader
      >
        <SpecializationContent onPositionSlotIdChange={setCurrentPositionSlotId} />
      </FormShowTemplate>

      <Modal
        isOpen={Boolean(warningResult)}
        title="Position slot may no longer fit"
        onToggle={handleWarningCancel}
        buttons={[warningSubmitButton, warningCancelButton]}
        hasCancelButton={false}
        paperStyles={{ background: 'var(--mui-palette-background-paper)' }}
      >
        <Box>
          <Typography>
            The current position slot may not match the updated employee data. Review the validation details below. You
            can save the changes anyway or return to editing.
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
            {warningResult?.errors.map(error => (
              <Box key={error} component="li">
                <Typography component="span" variant="body2">
                  {error}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SpecializationInfoSection;
